import queryString from 'query-string'

import { getCurrentAppConfig, getCurrentAppConfigApiHost } from '../config/appConfig'
import { USER_TOKEN_KEY_IN_STORAGE } from '../constants/user'

type Method = "GET" | "POST" | "PUT" | "DELETE" | "PATCH" | "HEAD"

export type Options = {
  headers?: {
    [key: string]: string
  },
  host?: string | null,
  apiVersion?: number,
  isUploadLog?: boolean,
  signal?: AbortSignal
}

type Data = {
  [key: string]: any
}

const extractOrigin = (value?: string | null): string | null => {
  if (!value) {
    return null
  }

  try {
    return new URL(value).origin
  } catch (error) {
    return null
  }
}

const AUTH_ALLOWED_ORIGINS = (() => {
  const origins = new Set<string>()

  const register = (value?: string | null) => {
    const origin = extractOrigin(value)
    if (origin) {
      origins.add(origin)
    }
  }

  register(getCurrentAppConfigApiHost())

  try {
    const config = getCurrentAppConfig()
    register((config as any)?.api)
    register((config as any)?.authApi)
    register((config as any)?.provisioningApi)
  } catch (_error) {
    // Swallow errors so request() still works even if config is not yet initialised
  }

  return origins
})()

const isAllowedAuthHost = (host?: string | null): boolean => {
  if (host === undefined || host === null || host === '') {
    return true
  }

  const origin = extractOrigin(host)

  if (!origin) {
    return false
  }

  return AUTH_ALLOWED_ORIGINS.has(origin)
}

const getDefaultHeaders = (options?: Options) => {
  return !options?.isUploadLog && {
    Accept: 'application/json',
    "Mountbit-User-Agent": "web-client",
    'Cache-Control': 'no-cache'
  }
}

export const request = async <T>(method: Method, url: string, data?: Data, options?: Options): Promise<T> => {
  const uri = `${options?.host === null ? '' : options?.host || getCurrentAppConfigApiHost()}${createUrl(method, url, data)}`
  const headers = new Headers({
    ...getDefaultHeaders(options),
    ...createContentType(method, options),
    ...createTokenHeader(getToken(), options),
    ...options?.headers
  })

  console.log('headers', headers.get('Mountbit-Auth'))

  const config = new Request(uri, {
    method,
    headers,
    body: createBody(method, data),
    signal: options?.signal,
  })

  const response = await fetch(config)

  const responseHeaders: Record<string, string> = {}

  response.headers.forEach((value, key) => {
    responseHeaders[key] = value
  })

  const status = response.status
  const contentType = response.headers.get("Content-Type") || ""

  let responseData: any

  if (contentType.includes("application/json")) {
    try {
      responseData = await response.json()
    } catch (error) {
      responseData = {}
    }
  } else if (contentType.includes("text/html")) {
    responseData = await response.text()
  } else {
    responseData = await response.text()
  }

  if (status < 200 || status >= 400) {
    const errorPayload = responseData ?? {}

    // Include raw HTTP metadata so callers can distinguish auth failures from transient issues
    throw {
      ...errorPayload,
      data: errorPayload,
      headers: responseHeaders,
      status,
      statusText: response.statusText
    }
  }

  if (!responseData) {
    responseData = {}
  }

  if (typeof responseData === "object") {
    responseData.headers = { ...responseHeaders }
  }

  return responseData
}

const createContentType = (method: Method, options?: Options): {"Content-Type": string} | null => {
  if (method !== 'GET' && !options?.isUploadLog) {
    return { "Content-Type": 'application/json' }
  }

  return null
}

const createTokenHeader = (token: string | null, options?:Options): ({"Mountbit-Auth": string} | null) => {
  if (!token || options?.isUploadLog || options?.headers?.hasOwnProperty('Transformation-Params')) {
    return null
  }

  if (!isAllowedAuthHost(options?.host)) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[request] Skipping Mountbit-Auth for untrusted host', options?.host)
    }

    return null
  }

  return { "Mountbit-Auth": token }
}

const getToken = () => localStorage.getItem(USER_TOKEN_KEY_IN_STORAGE)

const createBody = (method: Method, data?: Data | FormData): FormData | string | File | undefined => {
  if (data instanceof File || data instanceof FormData) {
    return data
  }

  if (method !== 'GET' && method !== 'HEAD' && data) {
    return JSON.stringify(data)
  }

  return undefined
}

const createUrl = (method: Method, url: string, data?: Data) => {
  if (method === 'GET' && data && !!Object.keys(data).length) {
    return `${url}?${queryString.stringify(data)}`
  }

  return url
}
